import { useState, useEffect, useCallback } from 'react';

// Helper function to determine if human intervention is required
const checkIfRequiresHuman = (rule: any, logs: any): boolean => {
  // Ensure logs is an array
  const logsArray = Array.isArray(logs) ? logs : [];
  
  // Check recent execution logs for escalation indicators
  const recentLogs = logsArray.slice(0, 3); // Check last 3 executions
  
  for (const log of recentLogs) {
    // Check for explicit escalation
    if (log.executionStatus === 'escalated' || log.errorMessage?.includes('HUMAN_REQUIRED') || log.errorMessage?.includes('human_required')) {
      return true;
    }
    
    // Check for low confidence responses
    if (log.confidenceScore && log.confidenceScore < (rule.confidenceThreshold || 0.7)) {
      return true;
    }
    
    // Check for negative sentiment threshold breach
    if (log.aiAnalysisResult) {
      try {
        const analysis = typeof log.aiAnalysisResult === 'string' 
          ? JSON.parse(log.aiAnalysisResult) 
          : log.aiAnalysisResult;
        
        if (analysis.sentiment?.score < 0.3 && rule.sentimentThreshold === 'negative') {
          return true;
        }
        
        // Check for escalation keywords in analysis
        if (analysis.requiresHuman || analysis.escalationRequired) {
          return true;
        }
      } catch (e) {
        // If we can't parse the analysis, it might indicate an issue
        console.warn('Failed to parse AI analysis result:', e);
      }
    }
    
    // Check for specific error conditions
    if (log.errorMessage && (
      log.errorMessage.includes('complexity_too_high') ||
      log.errorMessage.includes('sentiment_too_negative') ||
      log.errorMessage.includes('escalation_keywords_detected')
    )) {
      return true;
    }
  }
  
  return false;
};

export interface AutomationStatus {
  id: string;
  conversationId: string;
  isEnabled: boolean;
  activationType: 'immediate' | 'on_response';
  isCurrentlyActive: boolean;
  currentResponseCount: number;
  maxResponses: number;
  lastAutomatedResponseAt?: string;
  
  // Status derived from current state
  status: 'active' | 'dormant' | 'waiting' | 'paused' | 'completed' | 'requires_human' | 'not_configured';
  
  // Next scheduled message info
  nextScheduledMessage?: {
    id: string;
    scheduledFor: string;
    content: string;
    delayMinutes: number;
    confidence: number;
  };
  
  // Recent execution logs
  recentExecutions: Array<{
    id: string;
    triggeredAt: string;
    executionStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
    confidence?: number;
    responseSent: boolean;
    errorMessage?: string;
  }>;
}

interface UseAutomationStatusReturn {
  automationStatus: AutomationStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getStatusColor: () => string;
  getStatusIcon: () => string;
  getStatusLabel: () => string;
  getTimeToNextMessage: () => string | null;
}

export const useAutomationStatus = (conversationId: string): UseAutomationStatusReturn => {
  const [automationStatus, setAutomationStatus] = useState<AutomationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAutomationStatus = useCallback(async () => {
    if (!conversationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch automation rule
      const ruleResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/automation/${conversationId}`,
        {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (ruleResponse.status === 404) {
        // 404 is expected when no automation is configured - this is normal
        setAutomationStatus(null);
        setError(null); // Clear any previous errors
        return;
      }

      if (!ruleResponse.ok) {
        // Only throw for actual errors, not 404s
        throw new Error(`Failed to fetch automation rule: ${ruleResponse.status}`);
      }

      const ruleResult = await ruleResponse.json();
      
      if (!ruleResult.success) {
        throw new Error(ruleResult.error || 'Failed to fetch automation rule');
      }

      const rule = ruleResult.data;

      // Handle case where no automation rule exists (data is null or undefined)
      if (!rule || rule === null || rule === undefined) {
        // No automation rule configured - this is normal
        console.log('📭 No automation rule found (this is normal)');
        setAutomationStatus(null);
        setError(null);
        return;
      }

      // Fetch execution logs
      const logsResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/automation/${conversationId}/logs?limit=10`,
        {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      let executionLogs = [];
      if (logsResponse.ok) {
        const logsResult = await logsResponse.json();
        if (logsResult.success) {
          executionLogs = logsResult.data.logs || [];
        }
      }

      // Double-check rule is still valid before accessing properties
      if (!rule || typeof rule !== 'object') {
        console.log('📭 Invalid rule data received');
        setAutomationStatus(null);
        setError(null);
        return;
      }

      // Determine current status
      let status: AutomationStatus['status'] = 'not_configured';
      let nextScheduledMessage: AutomationStatus['nextScheduledMessage'] = undefined;

      // Safe property access with fallbacks
      const isEnabled = rule.isEnabled === true;
      const currentCount = rule.currentResponseCount || 0;
      const maxResponses = rule.maxResponses || 5;
      const activationType = rule.activationType || 'immediate';

      if (isEnabled) {
         const requiresHuman = checkIfRequiresHuman(rule, executionLogs);
         
         if (requiresHuman) {
           status = 'requires_human';
         } else if (currentCount >= maxResponses) {
           status = 'completed';
         } else {
           const recentExecution = Array.isArray(executionLogs) ? executionLogs.find((log: any) => 
             log && log.executionStatus === 'processing' && log.scheduledFor && !log.responseSent
           ) : null;
           
           if (recentExecution && (recentExecution as any).scheduledDelayMinutes) {
             status = 'active';
             const execution = recentExecution as any;
             const scheduledFor = new Date(execution.scheduledFor).toISOString();
             
             nextScheduledMessage = {
               id: execution.id,
               scheduledFor,
               content: execution.finalResponse || 'Automated response',
               delayMinutes: execution.scheduledDelayMinutes,
               confidence: execution.confidenceScore || 0
             };
           } else if (activationType === 'on_response') {
             status = 'waiting';
           } else if (activationType === 'immediate') {
             status = 'active';
           }
         }
       } else {
         status = 'dormant';
       }

      const automationStatus: AutomationStatus = {
        id: rule.id || conversationId,
        conversationId: rule.conversationId || conversationId,
        isEnabled: isEnabled,
        activationType: activationType,
        isCurrentlyActive: rule.isCurrentlyActive || false,
        currentResponseCount: currentCount,
        maxResponses: maxResponses,
        lastAutomatedResponseAt: rule.lastAutomatedResponseAt || undefined,
        status,
        nextScheduledMessage,
        recentExecutions: executionLogs.slice(0, 5).map((log: any) => ({
          id: log.id,
          triggeredAt: log.triggeredAt,
          executionStatus: log.executionStatus,
          confidence: log.confidenceScore,
          responseSent: log.responseSent,
          errorMessage: log.errorMessage
        }))
      };

      setAutomationStatus(automationStatus);
      
    } catch (err) {
      console.error('Failed to fetch automation status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setAutomationStatus(null);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Auto-refresh every 30 seconds for active automations
  useEffect(() => {
    fetchAutomationStatus();
    
    const interval = setInterval(() => {
      if (automationStatus?.status === 'active') {
        fetchAutomationStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [conversationId, fetchAutomationStatus, automationStatus?.status]);

  const getStatusColor = useCallback((): string => {
    if (!automationStatus) return 'text-gray-400';
    
    switch (automationStatus.status) {
      case 'active':
        return 'text-green-500';
      case 'waiting':
        return 'text-blue-500';
      case 'dormant':
        return 'text-yellow-500';
      case 'paused':
        return 'text-orange-500';
      case 'completed':
        return 'text-purple-500';
      case 'requires_human':
        return 'text-red-500';
      case 'not_configured':
      default:
        return 'text-gray-400';
    }
  }, [automationStatus]);

  const getStatusIcon = useCallback((): string => {
    if (!automationStatus) return '⚪';
    
    switch (automationStatus.status) {
      case 'active':
        return '🟢';
      case 'waiting':
        return '🔵';
      case 'dormant':
        return '🟡';
      case 'paused':
        return '🟠';
      case 'completed':
        return '🟣';
      case 'requires_human':
        return '🔴';
      case 'not_configured':
      default:
        return '⚪';
    }
  }, [automationStatus]);

  const getStatusLabel = useCallback((): string => {
    if (!automationStatus) return 'No automation';
    
    switch (automationStatus.status) {
      case 'active':
        return automationStatus.activationType === 'immediate' ? 'Active Now' : 'Active on Response';
      case 'waiting':
        return 'Waiting for Response';
      case 'dormant':
        return 'Configured but Inactive';
      case 'paused':
        return 'Temporarily Paused';
      case 'completed':
        return 'Automation Complete';
      case 'requires_human':
        return 'Requires Human Intervention';
      case 'not_configured':
      default:
        return 'Not Configured';
    }
  }, [automationStatus]);

  const getTimeToNextMessage = useCallback((): string | null => {
    if (!automationStatus?.nextScheduledMessage) return null;
    
    const now = new Date();
    const scheduledTime = new Date(automationStatus.nextScheduledMessage.scheduledFor);
    const diffMs = scheduledTime.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Sending now...';
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (diffMinutes > 0) {
      return `${diffMinutes}m ${diffSeconds}s`;
    } else {
      return `${diffSeconds}s`;
    }
  }, [automationStatus]);

  return {
    automationStatus,
    loading,
    error,
    refresh: fetchAutomationStatus,
    getStatusColor,
    getStatusIcon,
    getStatusLabel,
    getTimeToNextMessage
  };
}; 